import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import { Box, HStack, IconButton, Image, Link, Stack, useBreakpointValue } from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"

interface Props {
  partners: any[]
}

export function PartnersSlider({ partners }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(0)

  const isMobile = useBreakpointValue({ base: true, md: false })
  const displayCount = isMobile ? 3 : 8

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
      setCurrentIndex(0)
    }
  }, [isMobile, emblaApi])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const canScrollPrev = currentIndex > 0
  const canScrollNext = currentIndex + displayCount < partners.length

  return (
    <Box
      id="my-embla-carousel-partners"
      position="relative"
      bgColor="white"
      p={8}
      borderRadius="xl"
      pr={20}
    >
      {partners.length > displayCount && (
        <>
          <Stack zIndex={500} position="absolute" top="0px" bottom="0px" left={0} justify="center">
            <IconButton
              colorScheme="black"
              variant="solid"
              aria-label="vorige dia"
              icon={<MdKeyboardArrowLeft size={48} />}
              onClick={(e) => {
                e.stopPropagation()
                scrollPrev()
              }}
              disabled={!canScrollPrev}
              width="3rem"
            />
          </Stack>
          <Stack zIndex={500} position="absolute" top="0px" bottom="0px" right={0} justify="center">
            <IconButton
              colorScheme="black"
              variant="solid"
              aria-label="volgende dia"
              icon={<MdKeyboardArrowRight size={48} />}
              onClick={(e) => {
                e.stopPropagation()
                scrollNext()
              }}
              disabled={!canScrollNext}
              width="3rem"
              ml={5}
            />
          </Stack>
        </>
      )}
      <Box className="embla" ref={emblaRef} overflow="hidden">
        <HStack className="embla__container" display="flex" spacing={0}>
          {partners.map(
            (partner) =>
              partner.url &&
              partner.imageUrl && (
                <Box
                  cursor="pointer"
                  key={partner.id}
                  className="embla__slide"
                  flex={`0 0 calc(${100 / displayCount}% - ${displayCount > 1 ? 15 : 0}px)`}
                  minW={0}
                  mr={displayCount > 1 ? "30px" : 0}
                >
                  <Link
                    href={partner.url}
                    isExternal
                    display="block"
                    _focus={{
                      outline: "2px solid",
                      outlineColor: "blue.500",
                      outlineOffset: "4px",
                      borderRadius: "md",
                    }}
                    tabIndex={0}
                  >
                    <Image alt="partnerlogo" src={partner.imageUrl} objectFit="cover" />
                  </Link>
                </Box>
              ),
          )}
        </HStack>
      </Box>
    </Box>
  )
}
